import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { getColorClass } from '../utils/layoutConfig';

/**
 * Get file icon based on file extension
 */
function getFileIcon(fileName) {
  if (!fileName) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
        <polyline points="13 2 13 9 20 9"/>
      </svg>
    );
  }

  const ext = fileName.split('.').pop()?.toLowerCase();

  // JavaScript/TypeScript
  if (['js', 'jsx', 'ts', 'tsx'].includes(ext)) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f7df1e" strokeWidth="2">
        <path d="M20 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
        <path d="M12 8v8"/>
        <path d="M8 12h8"/>
      </svg>
    );
  }

  // Python
  if (ext === 'py') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3776ab" strokeWidth="2">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
        <path d="M8 12h8"/>
      </svg>
    );
  }

  // CSS/Styles
  if (['css', 'scss', 'sass', 'less'].includes(ext)) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#264de4" strokeWidth="2">
        <path d="M4 3h16v18H4z"/>
        <path d="M8 7h8M8 12h6M8 17h4"/>
      </svg>
    );
  }

  // JSON/Config
  if (['json', 'yaml', 'yml', 'toml'].includes(ext)) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
        <path d="M17 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
        <path d="M8 10h8M8 14h5"/>
      </svg>
    );
  }

  // Default file icon
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
      <polyline points="13 2 13 9 20 9"/>
    </svg>
  );
}

/**
 * Format relative time for last modified
 */
function formatLastModified(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

/**
 * Tooltip content for detailed metrics
 */
function NodeTooltip({ data, isDir, coupling, isDepsMode }) {
  const metrics = data.metrics;

  return (
    <div className="node-tooltip">
      <div className="tooltip-header">{data.relativePath || data.label}</div>
      <div className="tooltip-content">
        {isDepsMode && coupling && (
          <>
            <div className="tooltip-row">
              <span className="tooltip-label">Fan-in</span>
              <span className="tooltip-value">{coupling.fanIn} dependents</span>
            </div>
            <div className="tooltip-row">
              <span className="tooltip-label">Fan-out</span>
              <span className="tooltip-value">{coupling.fanOut} dependencies</span>
            </div>
            {coupling.inCycle && (
              <div className="tooltip-row">
                <span className="tooltip-label">Cycle</span>
                <span className="tooltip-value" style={{ color: '#ef4444' }}>Part of dependency cycle</span>
              </div>
            )}
          </>
        )}
        {isDir && data.fileCount > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Files</span>
            <span className="tooltip-value">{data.fileCount}</span>
          </div>
        )}
        {data.symbolCount > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Symbols</span>
            <span className="tooltip-value">{data.symbolCount}</span>
          </div>
        )}
        {metrics?.loc > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Lines of Code</span>
            <span className="tooltip-value">{metrics.loc.toLocaleString()}</span>
          </div>
        )}
        {metrics?.avgComplexity > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Avg Complexity</span>
            <span className="tooltip-value">{metrics.avgComplexity.toFixed(1)}</span>
          </div>
        )}
        {metrics?.maxComplexity > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Max Complexity</span>
            <span className="tooltip-value">{metrics.maxComplexity}</span>
          </div>
        )}
        {metrics?.churn > 0 && (
          <div className="tooltip-row">
            <span className="tooltip-label">Commits (30d)</span>
            <span className="tooltip-value">{metrics.churn}</span>
          </div>
        )}
        {metrics?.lastModified && (
          <div className="tooltip-row">
            <span className="tooltip-label">Last Modified</span>
            <span className="tooltip-value">{formatLastModified(metrics.lastModified)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Custom node for files in the file dependency graph
 */
function FileNode({ data, selected }) {
  const isDir = data.isDirectory;
  const metrics = data.metrics;
  const colorMode = data.colorMode || 'complexity';
  const archMode = data.archMode || 'structure';
  const coupling = data.coupling;
  const isDepsMode = archMode === 'dependencies';
  const boundaryMode = data.boundaryMode;
  const boundary = data.boundary;

  // Determine color class based on mode
  let colorClass;
  if (boundaryMode && boundary) {
    // Boundary mode: use semantic layer coloring (applied via inline style)
    colorClass = `boundary-${boundary.layer}`;
  } else if (isDepsMode && coupling?.level) {
    // Dependencies mode: use coupling level
    colorClass = `coupling-${coupling.level}`;
  } else {
    // Default: use metrics-based coloring
    colorClass = getColorClass(colorMode, metrics);
  }

  // Always apply boundary color to left border when available (semantic layer indicator)
  const boundaryStyle = boundary?.color
    ? { borderLeftColor: boundary.color }
    : {};

  const hasTooltipData = metrics || data.fileCount > 0 || coupling;
  const searchClass = data.searchMatch === 'dimmed' ? 'search-dimmed' : data.searchMatch === 'match' ? 'search-match' : '';
  const cycleClass = isDepsMode && coupling?.inCycle ? 'in-cycle' : '';
  const cycleHighlightClass = data.cycleHighlight ? 'cycle-highlight' : '';
  const fanInHighlightClass = data.fanInHighlight ? 'fan-in-highlight' : '';
  const hasChildrenClass = data.hasChildren ? 'has-children' : '';
  const entryPointClass = data.entryPointHighlight ? 'entry-point-highlight' : '';

  // Handle positions: All views use TB layout = Top/Bottom
  const targetPos = Position.Top;
  const sourcePos = Position.Bottom;

  return (
    <div
      className={`graph-node file-node ${isDir ? 'is-directory' : ''} ${selected ? 'selected' : ''} ${colorClass} ${cycleClass} ${cycleHighlightClass} ${fanInHighlightClass} ${searchClass} ${hasChildrenClass} ${entryPointClass}`}
      style={boundaryStyle}
    >
      <Handle type="target" position={targetPos} />
      <div className="node-icon">
        {isDepsMode && coupling?.inCycle ? (
          // Cycle warning icon
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        ) : isDir ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
        ) : (
          getFileIcon(data.fileName)
        )}
      </div>
      <div className="node-content">
        <div className="node-header">
          <div className="node-label">{data.label}</div>
          {boundary?.label && (
            <span
              className="role-badge"
              style={{ backgroundColor: boundary.color }}
              title={`Inferred role: ${boundary.label}${boundary.source === 'ckb' ? ' (from CKB)' : ' (inferred from path)'}`}
            >
              {boundary.label}
            </span>
          )}
        </div>
        {isDepsMode && coupling ? (
          // Dependencies mode: show coupling metrics
          <div className="node-coupling">
            <span className="coupling-metric" title="Fan-in: directories that depend on this">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {coupling.fanIn}
            </span>
            <span className="coupling-metric" title="Fan-out: directories this depends on">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="18 15 12 9 6 15"/>
              </svg>
              {coupling.fanOut}
            </span>
            {coupling.inCycle && (
              <span className="cycle-badge" title="Part of a dependency cycle">cycle</span>
            )}
          </div>
        ) : (
          // Structure/Files mode: show file count and metrics
          <>
            {isDir && (data.fileCount > 0 || data.symbolCount > 0) && (
              <div className="node-meta">
                {data.fileCount > 0 && <span>{data.fileCount} files</span>}
                {data.fileCount > 0 && data.symbolCount > 0 && <span> · </span>}
                {data.symbolCount > 0 && <span>{data.symbolCount} symbols</span>}
              </div>
            )}
            {metrics && (metrics.loc > 0 || metrics.avgComplexity > 0) && (
              <div className="node-metrics">
                {metrics.loc > 0 && (
                  <span className="metric">{metrics.loc.toLocaleString()} LOC</span>
                )}
                {metrics.avgComplexity > 0 && (
                  <span className="metric metric-complexity">⚡ {metrics.avgComplexity.toFixed(1)}</span>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {hasTooltipData && <NodeTooltip data={data} isDir={isDir} coupling={coupling} isDepsMode={isDepsMode} />}
      <Handle type="source" position={sourcePos} />
    </div>
  );
}

export default memo(FileNode);
